import { useState } from "react";
import { Link } from "wouter";
import { Users, Share2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { useChatRooms } from "@/lib/queries/community";

export function CommunicateSidebar({ 
  activeView, 
  setActiveView, 
  activeUserId, 
  setActiveUserId 
}: { 
  activeView: "inbox" | "people" | "shares";
  setActiveView: (view: "inbox" | "people" | "shares") => void;
  activeUserId: string | null;
  setActiveUserId: (id: string | null) => void;
}) {
  const { data: threads = [] } = useQuery<any[]>({
    queryKey: ["/api/communicate/threads"],
    queryFn: async () => {
      const res = await fetch("/api/communicate/threads");
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 10000,
  });

  const { data: chatRooms = [] } = useChatRooms();

  return (
    <div
      className={`${activeUserId || activeView !== "inbox" ? "hidden md:flex" : "flex"} w-full md:w-64 lg:w-72 shrink-0 border-r flex-col bg-card`}
    >
      <div className="px-4 py-4 border-b">
        <h1 className="text-xl font-bold text-foreground">Communicate</h1>
      </div>

      <div className="p-2 space-y-1 border-b">
        <Button 
          variant={activeView === "inbox" && !activeUserId ? "secondary" : "ghost"} 
          className="w-full justify-start h-9 px-3" 
          onClick={() => { setActiveView("inbox"); setActiveUserId(null); }}
        >
          <MessageSquare className="w-4 h-4 mr-2" /> Inbox
        </Button>
        <Button 
          variant={activeView === "people" ? "secondary" : "ghost"} 
          className="w-full justify-start h-9 px-3" 
          onClick={() => { setActiveView("people"); setActiveUserId(null); }}
          data-testid="button-communicate-people"
        >
          <Users className="w-4 h-4 mr-2" /> People
        </Button>
        <Button 
          variant={activeView === "shares" ? "secondary" : "ghost"} 
          className="w-full justify-start h-9 px-3" 
          onClick={() => { setActiveView("shares"); setActiveUserId(null); }}
          data-testid="button-communicate-shares"
        >
          <Share2 className="w-4 h-4 mr-2" /> Shares
        </Button>
      </div>

      <ScrollArea className="flex-1 px-2 py-2">
        <div className="space-y-4">
          {threads.length > 0 && (
            <div>
              <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Direct Messages</h3>
              <div className="space-y-0.5">
                {threads.map(t => (
                  <Button
                    key={t.other_user_id}
                    variant={activeUserId === t.other_user_id ? "secondary" : "ghost"}
                    className="w-full justify-start h-10 px-3 font-normal"
                    onClick={() => { setActiveView("inbox"); setActiveUserId(t.other_user_id); }}
                  >
                    <div className="truncate flex-1 text-left">{t.other_user_name}</div>
                    {t.unread_count > 0 && <span className="ml-auto bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full">{t.unread_count}</span>}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {chatRooms.length > 0 && (
            <div>
              <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Group Chats</h3>
              <div className="space-y-0.5">
                {chatRooms.map(r => (
                  <Button
                    key={r.id}
                    variant={activeUserId === `room_${r.id}` ? "secondary" : "ghost"}
                    className="w-full justify-start h-10 px-3 font-normal"
                    onClick={() => { setActiveView("inbox"); setActiveUserId(`room_${r.id}`); }}
                  >
                    <div className="truncate flex-1 text-left">{r.name}</div>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
